import { useFeatureValue } from '@growthbook/growthbook-react';
import { FtbVirtualCourtConfigData, StatscoreConfigData, StatscoreGroupType } from '../@types';
import { useMarketplaceConfig } from '../queries/marketplace';
import { useMemo } from 'react';

const DEFAULT_WIDGET_FEATURES = {
  preMatchWidget: false,
  liveWidget: false,
} as const;

const useMatchWidgetConfig = (type: StatscoreGroupType, sportId: string) => {
  const { isFTBFeed, data: marketplaceConfig } = useMarketplaceConfig();
  const config = useFeatureValue<StatscoreConfigData | null>('statscore-config', null);
  const ftbConfig = useFeatureValue<FtbVirtualCourtConfigData | null>('ftb-virtual-court-config', null);

  const origin = useMemo(() => new URL(window.location.origin).hostname, []);
  const widgetFeatures = marketplaceConfig?.sportsbookConfig.widgetFeatures ?? DEFAULT_WIDGET_FEATURES;
  const isMarketplaceWidgetEnabled = type === 'liveMatch' ? widgetFeatures.liveWidget : widgetFeatures.preMatchWidget;

  const ftbWidgetAvailable = useMemo(() => {
    const widgetSportIds = type === 'liveMatch' ? ftbConfig?.liveWidgetSportIds : ftbConfig?.preMatchWidgetSportIds;

    return isFTBFeed && !!widgetSportIds?.includes(sportId);
  }, [isFTBFeed, ftbConfig, sportId, type]);

  const { statscoreWidgetAvailable, groupConfigurationId } = useMemo(() => {
    if (isFTBFeed || !config) {
      return { statscoreWidgetAvailable: false, groupConfigurationId: undefined };
    }

    const configData = config[origin];
    const coverage = type === 'liveMatch' ? configData?.liveMatchSportsCoverage : configData?.prematchSportsCoverage;
    const configId = type === 'liveMatch' ? configData?.liveMatchConfigurationId : configData?.prematchConfigurationId;

    const isCovered = !!coverage?.includes(sportId);
    return {
      statscoreWidgetAvailable: !!configId && isCovered,
      groupConfigurationId: configId,
    };
  }, [isFTBFeed, config, origin, sportId, type]);

  return {
    groupConfigurationId: isFTBFeed ? undefined : groupConfigurationId,
    isWidgetAvailable: isMarketplaceWidgetEnabled && (isFTBFeed ? ftbWidgetAvailable : statscoreWidgetAvailable),
    displayCourtIcon: isMarketplaceWidgetEnabled && statscoreWidgetAvailable,
  };
};

export default useMatchWidgetConfig;
